import type { ApiResponse } from '@skytech/shared';

/**
 * Fetch wrapper around the SRS §6.1 envelope with transparent token refresh.
 *
 * The access token lives in memory only (SRS §10.1) — never localStorage. On a 401
 * the client calls the registered refresh handler once (which reads the HttpOnly
 * refresh cookie via /auth/refresh) and retries the original request. A single
 * in-flight refresh is shared so a burst of 401s triggers only one refresh.
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

let accessToken: string | null = null;
let refreshHandler: (() => Promise<string | null>) | null = null;
let inFlightRefresh: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registered by the auth store; performs the /auth/refresh round-trip. */
export function setRefreshHandler(handler: (() => Promise<string | null>) | null): void {
  refreshHandler = handler;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function raw(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });
}

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiResponse<T>> {
  let response = await raw(path, init, accessToken);

  // Refresh-and-retry once, but never for the auth endpoints themselves.
  if (response.status === 401 && refreshHandler && !path.startsWith('/auth/')) {
    inFlightRefresh ??= refreshHandler().finally(() => {
      inFlightRefresh = null;
    });
    const newToken = await inFlightRefresh;
    if (newToken) response = await raw(path, init, newToken);
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await response.json() : null;

  if (!response.ok) {
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : (body?.message ?? response.statusText);
    const retryAfter = response.headers.get('Retry-After');
    const seconds = retryAfter ? Number(retryAfter) : NaN;
    throw new ApiError(
      response.status,
      message,
      Number.isFinite(seconds) && seconds >= 0 ? Math.min(seconds, 30) : undefined,
    );
  }
  return body as ApiResponse<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  put: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PUT', body: data ? JSON.stringify(data) : undefined }),
  delete: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'DELETE', body: data ? JSON.stringify(data) : undefined }),
};
